import { css } from 'aphrodite';
import * as React from 'react';
import { IAccount } from '../../../extViewmodels';
import { Button } from '../../../web/components-v2/Button';
import { Input } from '../../../web/components-v2/Input';
import { BrandSettingsSection } from '../../../web/components/settings/AdminSettings/BrandSettingsSection';
import { styleSheet } from './styles';
import { normalizeWebsiteUrl } from './websiteUrl';

interface IProps {
	/** The account whose brand settings are being configured. */
	account: IAccount;
	/**
	 * Website URL to offer when the account has none on file. Seeded from whatever the rep already
	 * entered on an earlier post-meeting tab, so they do not retype it here.
	 */
	websiteUrl: string;
	onWebsiteUrlChange: (value: string) => void;
	/** Persists `websiteUrl` to the account so brand discovery can find it. */
	onSaveWebsiteUrl: () => void;
	isSavingWebsiteUrl: boolean;
	/** True once this flow has saved the URL to the account, which the stale `account` will not show. */
	isWebsiteUrlSaved: boolean;
	/** Marks the tab complete and moves on. Brand edits are saved by BrandSettingsSection itself. */
	onDone: () => void;
}

/**
 * Post-meeting "Brand Settings" tab. Wraps the same BrandSettingsSection the admin account detail
 * page uses, scoped to the meeting's account via impersonation context, so brand colors/logo can be
 * auto-discovered right after the meeting note is recorded.
 *
 * The full account (not just its id) is passed as the impersonation context because the section
 * reads `account.preferences.logo.url` to offer the "Apply existing logo" shortcut.
 */
export const BrandSettingsTab: React.FC<IProps> = ({
	account,
	websiteUrl,
	onWebsiteUrlChange,
	onSaveWebsiteUrl,
	isSavingWebsiteUrl,
	isWebsiteUrlSaved,
	onDone,
}) => {
	const impersonationContext = React.useMemo(() => ({ account }), [account]);
	// The branding agent reads the account's saved website URL, so asking for it here only helps once
	// it has been persisted - hence the prompt is shown only while the account still has none. The
	// `account` prop is not refetched after a save, so the local flag is what clears the prompt.
	const needsWebsiteUrl = !account.additionalInfo?.websiteUrl && !isWebsiteUrlSaved;
	// Persisted to the account and rendered as a link elsewhere in admin, so only http(s) may be saved.
	const isWebsiteUrlValid = normalizeWebsiteUrl(websiteUrl) !== null;

	return (
		<div className={css(styleSheet.confirmationColumn, styleSheet.confirmationColumnFull)}>
			<div className={css(styleSheet.summaryHeader)}>
				<h3>Brand Settings</h3>
			</div>
			<div className={css(styleSheet.summaryNote)}>
				This account has no brand settings yet. Use Auto-Discover with AI to pull colors, fonts and logo from their
				website, or set them manually. Everything here is optional.
			</div>

			{needsWebsiteUrl && (
				<div className={css(styleSheet.deepResearchFields)}>
					<div>
						<label className={css(styleSheet.deepResearchFieldLabel)}>Website URL</label>
						<div className={css(styleSheet.brandSettingsUrlRow)}>
							<Input
								type='text'
								styles={[styleSheet.brandSettingsUrlInput]}
								value={websiteUrl}
								onChange={e => onWebsiteUrlChange(e.target.value)}
								placeholder='e.g., https://example.com'
								disabled={isSavingWebsiteUrl}
							/>
							<Button
								variant='secondary'
								size='sm'
								onClick={onSaveWebsiteUrl}
								isLoading={isSavingWebsiteUrl}
								disabled={isSavingWebsiteUrl || !isWebsiteUrlValid}
							>
								Save URL
							</Button>
						</div>
						<span className={css(styleSheet.brandSettingsUrlHint)}>
							{websiteUrl.trim() && !isWebsiteUrlValid
								? 'Enter a web address, for example example.com or https://example.com.'
								: 'Saved to the account so brand discovery knows which site to analyze.'}
						</span>
					</div>
				</div>
			)}

			<BrandSettingsSection impersonationContext={impersonationContext} />

			<div className={css(styleSheet.formActions)}>
				<Button variant='primary' onClick={onDone}>
					Done
				</Button>
			</div>
		</div>
	);
};
